import "./legend.css";

import { Flex, Text } from "@chakra-ui/react";
import React, { ReactNode } from "react";
import {
  Area,
  AreaChart,
  Label,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useTheme } from "../../../../components";
import colorVars from "../../../../theme/css-color-variables";
import { ReportConfig } from "../../../graphql";
import { BenchmarkLabel } from "../BenchmarkLabel";
import { dataVizColorRangesBright } from "../chart/segmentPresentation";
import { LineChartDataPoint, TopicLineChartDataPoint } from "../types";
import MyAnalyticsTooltip from "./MyAnalyticsTooltip";
import ReportTooltip from "./ReportTooltip";
import { xFormatter } from "./xFormatter";

type CartesianViewBox = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
};

export type AnalyticsAreaSeriesConfig = {
  dataKey: string;
  name: string;
  fill: string;
  stroke?: string;
};

export type AnalyticsLineChartProps = {
  tooltipDetailsFormatter?: (args: {
    datum: unknown;
    compareDatum?: unknown;
    formatLabel: (iso: string) => string;
  }) => Array<{
    label: string;
    value: string;
  }>;
  data: LineChartDataPoint[] | TopicLineChartDataPoint[];
  rangeDisplayValues: {
    previous: string;
    current: string;
  };
  columnsConfig?: ReportConfig[];
  bucketInterval?: string;
  bucketSize?: number;
  dataKey: string;
  compareDataKey?: string;
  xAxisTickInterval?: "firstAndLast" | "equidistantPreserveStart";
  yAxis?: {
    domain?: [number | string, number | string];
    ticks?: number[];
    formatter?: (value: number) => string;
    tickCount?: number;
    /**
     * Recharts defaults Y-axis `interval` to `"preserveEnd"`, which adjusts first/last tick
     * positions so labels fit the view—mid ticks stay on scale, so spacing looks uneven.
     * Use `0` to show ticks at true scale positions (same idea as `yAxisTick` + `interval: 0`).
     */
    interval?:
      | number
      | "preserveEnd"
      | "preserveStart"
      | "preserveStartEnd"
      | "equidistantPreserveStart";
  };
  tooltipVersion?: "report" | "myInsights";
  tooltipFooterText?: string; // Note: only used in myInsights tooltip
  tooltipFooterFormatter?: (datum: any) => string;
  benchmarkRange?: {
    upperBound: number;
    lowerBound: number;
  };
  benchmarkFormatter?: (value: number) => string;
  line?: {
    label: string;
    value: number;
  };
  showComparisonLine?: boolean;
  toggleComparisonLine?: () => void;
  aspect?: number;
  labelReferenceInLegend?: boolean;
  customActiveDot?: any;
  drawDot: boolean;
  lineColor?: string;
  lineStrokeWidth?: number;
  hideLegend?: boolean;
  yAxisTick?: ((props: any) => React.ReactElement) | React.ReactElement;
  chartMargin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  chartHeight?: number | string;
  /** When set, renders a stacked area chart instead of a line chart. */
  chartVariant?: "line" | "area";
  /** Series in stack order: first item is the bottom layer. */
  areaSeries?: AnalyticsAreaSeriesConfig[];
  areaStackId?: string;
  /** If provided, used as the Tooltip content (area mode or line mode). */
  customTooltipContent?: (
    props: Record<string, unknown>
  ) => React.ReactElement | null;
  /** When false, omits the `reverse-legend` class on the container (e.g. for area charts). */
  reverseLegend?: boolean;
};

const AnalyticsLineChart: React.FC<AnalyticsLineChartProps> = ({
  data,
  rangeDisplayValues,
  columnsConfig,
  bucketInterval,
  bucketSize,
  dataKey,
  compareDataKey,
  xAxisTickInterval = "equidistantPreserveStart",
  yAxis,
  tooltipVersion = "report",
  tooltipFooterText,
  benchmarkRange,
  benchmarkFormatter = (value) => value.toString(),
  line,
  showComparisonLine = true,
  toggleComparisonLine,
  aspect = 2.75,
  labelReferenceInLegend,
  customActiveDot,
  drawDot,
  lineColor,
  lineStrokeWidth,
  hideLegend,
  yAxisTick,
  chartMargin,
  chartHeight,
  tooltipFooterFormatter,
  tooltipDetailsFormatter,
  chartVariant = "line",
  areaSeries,
  areaStackId = "stack",
  customTooltipContent,
  reverseLegend = true,
}) => {
  const tickProps = {
    tick: { fontSize: "0.75rem", fill: colorVars.gray[600] },
    tickLine: false,
    tickMargin: 8,
  };

  const yAxisIntervalProps =
    yAxisTick != null
      ? { tick: yAxisTick, interval: 0 as const }
      : yAxis?.interval !== undefined
      ? { interval: yAxis.interval }
      : {};
  const yAxisTickCountProps =
    yAxis?.tickCount !== undefined ? { tickCount: yAxis.tickCount } : {};

  const theme = useTheme();
  const { colors } = theme;
  const { blueBolt } = dataVizColorRangesBright;

  const renderLegendText = (value: string, entry: any): ReactNode => {
    let { color } = entry;
    const legendLabelMap = {
      [dataKey]: rangeDisplayValues.current,
      referenceLine: line?.label,
    };
    if (compareDataKey) {
      legendLabelMap[compareDataKey] = rangeDisplayValues.previous;
    }
    if (benchmarkRange) {
      legendLabelMap.benchmarkRange = "Benchmark";
    }

    let suffix = " (Click to hide)";
    if (entry.inactive) {
      color = "gray.300";
      suffix = " (Click to show) ";
    }
    const canToggle = entry.dataKey === compareDataKey && toggleComparisonLine;
    return (
      <Text
        as="span"
        color={color}
        fontSize="xs"
        pr="4"
        cursor={canToggle ? "pointer" : undefined}
        _hover={{
          color: canToggle ? "blue.600" : undefined,
        }}
      >
        {legendLabelMap[value]}
        {canToggle && suffix}
      </Text>
    );
  };

  const dataMinMax =
    data.length > 1
      ? [Math.min(...data.map((d) => d.x)), Math.max(...data.map((d) => d.x))]
      : undefined;

  const areaLegendFormatter = (
    value: string,
    entry: { color?: string; dataKey?: unknown }
  ): ReactNode => {
    const dataKeyStr =
      typeof entry.dataKey === "string"
        ? entry.dataKey
        : Array.isArray(entry.dataKey)
        ? String(entry.dataKey[0])
        : undefined;
    let series: AnalyticsAreaSeriesConfig | undefined;
    if (dataKeyStr) {
      series = areaSeries?.find((s) => s.dataKey === dataKeyStr);
    }
    if (!series) {
      series = areaSeries?.find((s) => s.name === value);
    }
    const label = series?.name ?? value;
    return (
      <Text as="span" color={entry.color} fontSize="xs" pr="4">
        {label}
      </Text>
    );
  };

  if (chartVariant === "area" && areaSeries?.length) {
    return (
      <ResponsiveContainer
        width="99%"
        {...(chartHeight ? { height: chartHeight } : { aspect })}
        className={reverseLegend ? "reverse-legend" : undefined}
        debounce={1}
      >
        <AreaChart
          data={data}
          margin={chartMargin ?? { top: 8, right: 8, left: 8, bottom: 16 }}
        >
          <XAxis
            dataKey="x"
            type="number"
            domain={dataMinMax || ["dataMin", "dataMax"]}
            tickFormatter={(value) =>
              xFormatter(value, bucketInterval, bucketSize)
            }
            scale="time"
            stroke={colorVars.gray[200]}
            interval={
              xAxisTickInterval === "equidistantPreserveStart"
                ? xAxisTickInterval
                : undefined
            }
            ticks={
              xAxisTickInterval === "firstAndLast" && dataMinMax
                ? dataMinMax
                : undefined
            }
            {...tickProps}
          />
          <YAxis
            type="number"
            domain={yAxis?.domain}
            ticks={yAxis?.ticks}
            stroke={colorVars.gray[200]}
            tickFormatter={yAxis?.formatter}
            {...tickProps}
            {...yAxisTickCountProps}
            {...yAxisIntervalProps}
          />
          <Tooltip
            content={(props) =>
              customTooltipContent ? (
                customTooltipContent(props as Record<string, unknown>)
              ) : (
                <MyAnalyticsTooltip
                  bucketInterval={bucketInterval}
                  bucketSize={bucketSize}
                  dataKey={dataKey}
                  compareDataKey={
                    showComparisonLine ? compareDataKey : undefined
                  }
                  dataColor={blueBolt[700]}
                  compareDataColor={blueBolt[300]}
                  valueFormatter={yAxis?.formatter || ((v) => v.toString())}
                  detailsFormatter={tooltipDetailsFormatter}
                  footerText={tooltipFooterText}
                  footerFormatter={tooltipFooterFormatter}
                  {...props}
                />
              )
            }
          />
          {!hideLegend && (
            <Legend
              formatter={areaLegendFormatter}
              wrapperStyle={{ left: 8, bottom: 0 }}
            />
          )}
          {areaSeries.map((s) => (
            <Area
              key={s.dataKey}
              type="monotone"
              dataKey={s.dataKey}
              stackId={areaStackId}
              stroke={s.stroke ?? s.fill}
              fill={s.fill}
              name={s.name}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer
      width="99%"
      {...(chartHeight ? { height: chartHeight } : { aspect })}
      className={reverseLegend ? "reverse-legend" : undefined}
      debounce={1}
    >
      <LineChart
        data={data}
        margin={chartMargin ?? { top: 8, right: 8, left: 8, bottom: 16 }}
      >
        {benchmarkRange && (
          <>
            <ReferenceArea
              y1={benchmarkRange.lowerBound}
              y2={benchmarkRange.upperBound}
              strokeOpacity={0}
              fill={colors.gray[100]}
              fillOpacity={1}
              ifOverflow="extendDomain"
            >
              {!labelReferenceInLegend && (
                <Label
                  content={(props) => (
                    <LineChartBenchmarkLabel
                      formattedLowerBound={benchmarkFormatter(
                        benchmarkRange.lowerBound
                      )}
                      formattedUpperBound={benchmarkFormatter(
                        benchmarkRange.upperBound
                      )}
                      viewBox={props.viewBox as CartesianViewBox}
                    />
                  )}
                />
              )}
            </ReferenceArea>
            {labelReferenceInLegend && (
              <Line
                dataKey="benchmarkRange"
                stroke={colors.gray[300]}
                isAnimationActive={false}
                hide={!labelReferenceInLegend}
              />
            )}
          </>
        )}
        {line && (
          <>
            <ReferenceLine
              y={line.value}
              stroke={colors.green[500]}
              strokeWidth={2}
              strokeDasharray="4 4"
              transform="translate(0, -1)"
            />
            <Line
              dataKey="referenceLine"
              stroke={colors.green[500]}
              isAnimationActive={false}
            />
          </>
        )}
        <XAxis
          dataKey="x"
          type="number"
          domain={dataMinMax || ["dataMin", "dataMax"]}
          tickFormatter={(value) =>
            xFormatter(value, bucketInterval, bucketSize)
          }
          scale="time"
          stroke={colorVars.gray[200]}
          interval={
            xAxisTickInterval === "equidistantPreserveStart"
              ? xAxisTickInterval
              : undefined
          }
          ticks={
            xAxisTickInterval === "firstAndLast" && dataMinMax
              ? dataMinMax
              : undefined
          }
          {...tickProps}
        />
        <YAxis
          type="number"
          domain={yAxis?.domain}
          ticks={yAxis?.ticks}
          stroke={colorVars.gray[200]}
          tickFormatter={yAxis?.formatter}
          {...tickProps}
          {...yAxisTickCountProps}
          {...yAxisIntervalProps}
        />
        <Tooltip
          content={(props) =>
            customTooltipContent ? (
              customTooltipContent(props as Record<string, unknown>)
            ) : tooltipVersion === "report" && columnsConfig ? (
              <ReportTooltip
                columnsConfig={columnsConfig}
                bucketInterval={bucketInterval}
                bucketSize={bucketSize}
                {...props}
              />
            ) : (
              <MyAnalyticsTooltip
                bucketInterval={bucketInterval}
                bucketSize={bucketSize}
                dataKey={dataKey}
                compareDataKey={showComparisonLine ? compareDataKey : undefined}
                dataColor={blueBolt[700]}
                compareDataColor={blueBolt[300]}
                valueFormatter={
                  yAxis?.formatter || ((value) => value.toString())
                }
                detailsFormatter={tooltipDetailsFormatter}
                footerText={tooltipFooterText}
                footerFormatter={tooltipFooterFormatter}
                {...props}
              />
            )
          }
        />
        {!hideLegend && (
          <Legend
            formatter={renderLegendText}
            wrapperStyle={{ left: 8, bottom: 0 }}
            onClick={(e) => {
              if (e.dataKey === compareDataKey && toggleComparisonLine) {
                toggleComparisonLine();
              }
            }}
          />
        )}
        {compareDataKey && (
          <Line
            key={Math.random()} // force redraw to ensure line dots render
            strokeDasharray="4 4"
            connectNulls
            dataKey={compareDataKey}
            stroke={blueBolt[300]}
            strokeWidth={2}
            dot={drawDot}
            isAnimationActive={false}
            hide={!showComparisonLine}
          />
        )}
        <Line
          key={Math.random()} // force redraw to ensure line dots render
          connectNulls
          dataKey={dataKey}
          stroke={lineColor || blueBolt[700]}
          strokeWidth={lineStrokeWidth ?? 3}
          dot={drawDot}
          isAnimationActive={false}
          activeDot={customActiveDot}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const LineChartBenchmarkLabel: React.FC<{
  formattedLowerBound: string;
  formattedUpperBound: string;
  viewBox?: CartesianViewBox;
}> = ({ formattedLowerBound, formattedUpperBound, viewBox }) => {
  if (!viewBox) return null;

  return (
    <foreignObject {...viewBox} overflow="visible">
      <Flex w="100%" h="100%" justify="end">
        <BenchmarkLabel
          formattedLowerBound={formattedLowerBound}
          formattedUpperBound={formattedUpperBound}
          direction="row"
          pr="1"
          pt="1"
        />
      </Flex>
    </foreignObject>
  );
};

export default AnalyticsLineChart;
